import { useState, useMemo } from 'react'
import { colors, formatNumber } from '../styles/tokens'
import { Button, Input, Select } from './FormControls'
import { calculateDoorsWindows } from '../lib/quantityEngine'
import { useDraftPersistence } from '../lib/useDraftPersistence'

export default function DoorsWindowsModulePanel({ scopeId, boqItemId, superseding, onClose, onError, saving, onSave }) {
  const draftKey = `draft:doors_windows:${scopeId || boqItemId || 'unknown'}`
  const [draft, setDraft, clearDraft] = useDraftPersistence(draftKey, { items: [] })
  const initial = superseding ? { items: superseding.engineering_inputs?.items || [] } : draft
  const [items, setItemsRaw] = useState(initial.items)
  const setItems = (u) => setItemsRaw((p) => { const n = typeof u === 'function' ? u(p) : u; if (!superseding) setDraft((d) => ({ ...d, items: n })); return n })

  function addItem() { setItems((p) => [...p, { id: `I${Date.now()}`, itemType: 'door', width: '', height: '', geometryCount: '' }]) }
  function updateItem(id, patch) { setItems((p) => p.map((i) => (i.id === id ? { ...i, ...patch } : i))) }
  function removeItem(id) { setItems((p) => p.filter((i) => i.id !== id)) }

  const itemsForCalc = useMemo(() => items.map((i) => ({ itemType: i.itemType, width: Number(i.width) || undefined, height: Number(i.height) || undefined, geometryCount: i.geometryCount === '' ? NaN : Number(i.geometryCount) })), [items])

  const result = useMemo(() => {
    if (itemsForCalc.length === 0) return { value: null, error: null }
    try { return { value: calculateDoorsWindows(itemsForCalc), error: null } } catch (e) { return { value: null, error: e.message } }
  }, [itemsForCalc])

  async function handleSave() {
    if (!result.value) return
    const succeeded = await onSave({ calculatedValueTon: result.value.finalQuantity, unit: 'no', geometrySnapshot: { items: [] }, engineeringInputs: { items }, breakdownSnapshot: result.value, takeoffItemIdForProvenance: null })
    if (succeeded !== false) clearDraft()
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <label style={{ fontSize: 11, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase' }}>Items</label>
        <Button size="sm" variant="secondary" onClick={addItem}>+ Add Item</Button>
      </div>
      {items.map((i) => (
        <div key={i.id} style={{ border: `1px solid ${colors.border}`, borderRadius: 6, padding: 8, marginBottom: 8 }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
            <button onClick={() => removeItem(i.id)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: colors.danger, cursor: 'pointer' }}>✕</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))', gap: 6 }}>
            <L label="Type"><Select value={i.itemType} onChange={(e) => updateItem(i.id, { itemType: e.target.value })} style={{ fontSize: 11, width: '100%' }}><option value="door">Door</option><option value="window">Window</option></Select></L>
            <L label="Width (m, descriptive)"><Input type="number" value={i.width} onChange={(e) => updateItem(i.id, { width: e.target.value })} style={{ fontSize: 11, width: '100%' }} /></L>
            <L label="Height (m, descriptive)"><Input type="number" value={i.height} onChange={(e) => updateItem(i.id, { height: e.target.value })} style={{ fontSize: 11, width: '100%' }} /></L>
            <L label="Geometry Count (No.)"><Input type="number" value={i.geometryCount} onChange={(e) => updateItem(i.id, { geometryCount: e.target.value })} style={{ fontSize: 11, width: '100%' }} /></L>
          </div>
        </div>
      ))}
      {result.value && (
        <div style={{ margin: '12px 0', padding: '10px 12px', background: '#F8FAFF', border: `1px solid ${colors.primary}`, borderRadius: 8, fontWeight: 700 }}>
          Total: {formatNumber(result.value.finalQuantity)} No.
        </div>
      )}
      {result.error && <div style={{ fontSize: 12, color: colors.textMuted, fontStyle: 'italic', marginBottom: 14 }}>{result.error}</div>}
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        {!superseding && <button onClick={() => { if (window.confirm('Discard draft?')) { clearDraft(); onClose() } }} style={{ background: 'none', border: 'none', color: colors.danger, cursor: 'pointer', fontSize: 12 }}>Discard Draft</button>}
        <Button onClick={handleSave} disabled={!result.value || saving} style={{ marginLeft: 'auto' }}>{saving ? 'Saving…' : superseding ? 'Save New Version' : 'Save Output'}</Button>
      </div>
    </div>
  )
}
function L({ label, children }) { return <div><label style={{ display: 'block', fontSize: 10, color: colors.textMuted, marginBottom: 2 }}>{label}</label>{children}</div> }
